<p-toast></p-toast>

<app-page-shell>

    <app-page-detail-hero [auditStamp]="schedule()"
        eyebrow="Delivery Schedule"
        icon="pi pi-calendar"
        [title]="schedule().scheduleNumber || schedule().storeName || schedule().storeNumber || 'Delivery Schedule'"
        [subtitle]="(project()?.projectNumber || '-') + ' | ' + (project()?.projectName || '-')">
        <div tags>
            <p-tag [value]="installationStatusLabel()" [severity]="readinessSeverity(schedule().readinessStatus)"></p-tag>
            @if (supportsRemovalFlow() && schedule().removalReadinessStatus) {
                <p-tag [value]="removalStatusLabel()" [severity]="readinessSeverity(schedule().removalReadinessStatus)"></p-tag>
            }
            <p-tag [value]="executionProgressLabel()" [severity]="statusSeverity(schedule().progressStage || schedule().status)"></p-tag>
            <p-tag [value]="coverageModeLabel()" [severity]="coverageModeSeverity()"></p-tag>
            @if (schedule().allocationActionRequired !== false) {
                <p-tag [value]="allocationLabel()" [severity]="allocationSeverity(schedule().allocationStatus)"></p-tag>
            }
            @if (supportsRemovalFlow() && schedule().returnOutcomeStatus) {
                <p-tag [value]="returnOutcomeLabel()" [severity]="statusSeverity(schedule().returnOutcomeStatus)"></p-tag>
            }
        </div>
        <div actions>
            <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (click)="backToSchedule()"></p-button>
            @if (canEdit()) {
                <p-button label="Edit Schedule" icon="pi pi-pencil" (click)="editSchedule()"></p-button>
            }
        </div>
    </app-page-detail-hero>

    <div class="context-workbench">
        <app-page-data-panel
            title="Schedule Context"
            subtitle="Header values come from the POSM project. Store and stand values are linked to LogiLink and Warehousing master data.">
            <div class="section-box section-box-context">
                <div class="detail-summary">
                    <div>
                        <span>Schedule Number</span>
                        <strong>{{ schedule().scheduleNumber || '-' }}</strong>
                    </div>
                    <div>
                        <span>Client</span>
                        <button type="button" class="record-link" (click)="openClient()" [disabled]="!hasClientLink()">
                            <strong>{{ projectClientLabel() }}</strong>
                            <i class="pi pi-external-link"></i>
                        </button>
                    </div>
                    <div>
                        <span>Project / Job</span>
                        <button type="button" class="record-link" (click)="openProject()" [disabled]="!project()?.id">
                            <strong>{{ project()?.projectName || '-' }}</strong>
                            <i class="pi pi-external-link"></i>
                        </button>
                    </div>
                    <div>
                        <span>Store / Location</span>
                        <button type="button" class="record-link" (click)="openLocation()" [disabled]="!(schedule().location?.id || selectedStore()?.location?.id)">
                            <strong>{{ storeContextLabel() }}</strong>
                            <i class="pi pi-external-link"></i>
                        </button>
                    </div>
                    <div>
                        <span>{{ schedule().location?.retailer?.name || selectedStore()?.location?.retailer?.name ? 'Retailer / Store Chain' : 'Imported Customer Name' }}</span>
                        <strong>{{ schedule().location?.retailer?.name || selectedStore()?.location?.retailer?.name || schedule().customerName || '-' }}</strong>
                    </div>
                    <div>
                        <span>Default Contact</span>
                        <strong>{{ contactContextLabel() }}</strong>
                    </div>
                    <div>
                        <span>Warehousing Item</span>
                        <button type="button" class="record-link" (click)="openItem()" [disabled]="!(schedule().inventoryItem?.id || selectedItem()?.inventoryItem?.id)">
                            <strong>{{ itemContextLabel() }}</strong>
                            <i class="pi pi-external-link"></i>
                        </button>
                    </div>
                    <div>
                        <span>Source Row</span>
                        <strong>{{ schedule().sourceSheetName || '-' }}{{ schedule().sourceRowNumber ? ' #' + schedule().sourceRowNumber : '' }}</strong>
                    </div>
                </div>
            </div>
        </app-page-data-panel>

        <app-page-data-panel
            title="Execution Links"
            subtitle="Read-only execution state resolved from generated tasks, order releases, shipments, and delivery records.">
            <div class="section-box section-box-execution">
                <div class="execution-stream-grid" [class.execution-stream-grid--single]="!supportsRemovalFlow()">
                    <section class="execution-stream-card">
                        <div class="execution-stream-header">
                            <div>
                                <span class="stream-label">Installation Stream</span>
                                <strong>Warehouse to customer site</strong>
                            </div>
                            <p-tag [value]="friendlyStatusLabel(schedule().releaseStatus || schedule().shipmentStatus || schedule().taskStatus, 'Not started')" [severity]="statusSeverity(schedule().releaseStatus || schedule().shipmentStatus || schedule().taskStatus)"></p-tag>
                        </div>
                        <div class="execution-summary">
                            <div>
                                <span>Installation Task</span>
                                <strong>{{ schedule().installationTaskId ? ('Task #' + schedule().installationTaskId) : 'Not generated' }}</strong>
                                <p-tag [value]="friendlyStatusLabel(schedule().taskStatus, 'Not started')" [severity]="statusSeverity(schedule().taskStatus)"></p-tag>
                            </div>
                            <div>
                                <span>Order Release</span>
                                <button type="button" class="record-link" (click)="openRelease()" [disabled]="!schedule().transportReleaseId">
                                    <strong>{{ schedule().releaseNumber || (schedule().transportReleaseId ? ('Release #' + schedule().transportReleaseId) : 'No release') }}</strong>
                                    <i class="pi pi-external-link"></i>
                                </button>
                                <p-tag [value]="friendlyStatusLabel(schedule().releaseStatus || schedule().transportStatus, 'Not released')" [severity]="statusSeverity(schedule().releaseStatus || schedule().transportStatus)"></p-tag>
                            </div>
                            <div>
                                <span>Delivery</span>
                                <button type="button" class="record-link" (click)="openDelivery()" [disabled]="!schedule().deliveryId">
                                    <strong>{{ schedule().deliveryNumber || (schedule().deliveryId ? ('Delivery #' + schedule().deliveryId) : 'No delivery') }}</strong>
                                    <i class="pi pi-external-link"></i>
                                </button>
                                <p-tag [value]="friendlyStatusLabel(schedule().shipmentStatus, 'Pending')" [severity]="statusSeverity(schedule().shipmentStatus)"></p-tag>
                                <small>{{ formattedDate(schedule().actualDeliveryDate || schedule().statusDate) }}</small>
                            </div>
                            <div>
                                <span>Truck / Driver</span>
                                <strong>{{ schedule().truckName || '-' }}</strong>
                                <small>{{ schedule().driverName || '-' }}</small>
                            </div>
                        </div>
                    </section>

                    @if (supportsRemovalFlow()) {
                        <section class="execution-stream-card execution-stream-card-removal">
                            <div class="execution-stream-header">
                                <div>
                                    <span class="stream-label">Removal Stream</span>
                                    <strong>Customer site to return destination</strong>
                                </div>
                                <p-tag [value]="friendlyStatusLabel(schedule().removalReleaseStatus || schedule().removalShipmentStatus || schedule().removalTaskStatus, 'Not started')" [severity]="statusSeverity(schedule().removalReleaseStatus || schedule().removalShipmentStatus || schedule().removalTaskStatus)"></p-tag>
                            </div>
                            <div class="execution-summary">
                                <div>
                                    <span>Removal Task</span>
                                    <strong>{{ schedule().removalTaskId ? ('Task #' + schedule().removalTaskId) : 'Not generated' }}</strong>
                                    <p-tag [value]="friendlyStatusLabel(schedule().removalTaskStatus, 'Not started')" [severity]="statusSeverity(schedule().removalTaskStatus)"></p-tag>
                                </div>
                                <div>
                                    <span>Removal Release</span>
                                    <button type="button" class="record-link" (click)="openRemovalRelease()" [disabled]="!schedule().removalTransportReleaseId">
                                        <strong>{{ schedule().removalReleaseNumber || (schedule().removalTransportReleaseId ? ('Release #' + schedule().removalTransportReleaseId) : 'No release') }}</strong>
                                        <i class="pi pi-external-link"></i>
                                    </button>
                                    <p-tag [value]="friendlyStatusLabel(schedule().removalReleaseStatus || schedule().removalTransportStatus, 'Not released')" [severity]="statusSeverity(schedule().removalReleaseStatus || schedule().removalTransportStatus)"></p-tag>
                                </div>
                                <div>
                                    <span>Removal Delivery</span>
                                    <button type="button" class="record-link" (click)="openRemovalDelivery()" [disabled]="!schedule().removalDeliveryId">
                                        <strong>{{ schedule().removalDeliveryNumber || (schedule().removalDeliveryId ? ('Delivery #' + schedule().removalDeliveryId) : 'No delivery') }}</strong>
                                        <i class="pi pi-external-link"></i>
                                    </button>
                                    <p-tag [value]="friendlyStatusLabel(schedule().removalShipmentStatus, 'Pending')" [severity]="statusSeverity(schedule().removalShipmentStatus)"></p-tag>
                                </div>
                                <div>
                                    <span>Return Outcome</span>
                                    <strong>{{ returnOutcomeLabel() }}</strong>
                                    <small>{{ schedule().returnOutcomeReferenceNumber || (schedule().returnDisposition || 'Return path not set') }}</small>
                                    @if (schedule().returnOutcomeCompletedAt) {
                                        <small>{{ formattedDate(schedule().returnOutcomeCompletedAt) }}</small>
                                    }
                                </div>
                            </div>
                        </section>
                    }
                </div>
            </div>

        @if (schedule().mappingWarnings?.length) {
            <div class="warning-panel mt-3">
                <strong>Mapping Warnings</strong>
                <ul>
                    @for (warning of schedule().mappingWarnings; track warning) {
                        <li>{{ warning }}</li>
                    }
                </ul>
            </div>
        }
            @if (supportsRemovalFlow() && schedule().removalWarnings?.length) {
                <div class="warning-panel mt-3">
                    <strong>Removal Warnings</strong>
                    <ul>
                        @for (warning of schedule().removalWarnings; track warning) {
                            <li>{{ warning }}</li>
                        }
                    </ul>
                </div>
            }
            @if (supportsRemovalFlow() && schedule().returnOutcomeWarnings?.length) {
                <div class="warning-panel mt-3">
                    <strong>Return Outcome Warnings</strong>
                    <ul>
                        @for (warning of schedule().returnOutcomeWarnings; track warning) {
                            <li>{{ warning }}</li>
                        }
                    </ul>
                </div>
            }
            @if (supportsRemovalFlow() && hasRemovalStreamInput()) {
                <div class="section-box section-box-return mt-3">
                    <div class="return-outcome-header">
                        <div>
                            <strong>Return Outcome Completion</strong>
                            <p>Finish the warehouse receipt, destruction draft, or refurbishment follow-up only after the removal delivery is completed.</p>
                        </div>
                        <p-tag [value]="returnOutcomeLabel()" [severity]="statusSeverity(schedule().returnOutcomeStatus)"></p-tag>
                    </div>

                    <div class="grid mt-2">
                        @if (requiresWarehouseReturnCompletion()) {
                            <div class="col-12 lg:col-6">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-select
                                            [options]="returnZoneOptions()"
                                            optionLabel="label"
                                            optionValue="value"
                                            [filter]="true"
                                            [showClear]="true"
                                            [ngModel]="returnOutcomeZoneId()"
                                            (ngModelChange)="returnOutcomeZoneId.set($event)"
                                            [ngModelOptions]="{ standalone: true }"
                                            appendTo="body"
                                            styleClass="w-full"></p-select>
                                        <label>Receiving Zone</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                        }

                        @if (requiresConditionForReturnCompletion()) {
                            <div class="col-12 lg:col-6">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-check-circle"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-select
                                            [options]="returnConditionOptions()"
                                            optionLabel="label"
                                            optionValue="value"
                                            [filter]="true"
                                            [showClear]="true"
                                            [ngModel]="returnOutcomeConditionId()"
                                            (ngModelChange)="returnOutcomeConditionId.set($event)"
                                            [ngModelOptions]="{ standalone: true }"
                                            appendTo="body"
                                            styleClass="w-full"></p-select>
                                        <label>Return Condition</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                        }

                        <div class="col-12">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <textarea
                                        pTextarea
                                        rows="3"
                                        [ngModel]="returnOutcomeNotes()"
                                        (ngModelChange)="returnOutcomeNotes.set($event || '')"
                                        [ngModelOptions]="{ standalone: true }"
                                        class="w-full"></textarea>
                                    <label>Return Completion Notes</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </div>
                    </div>

                    <div class="return-outcome-actions">
                        <small class="field-note">
                            Destination:
                            <strong>
                                {{ schedule().returnWarehouseName || schedule().returnLocationName || (schedule().returnDestinationType || 'Not selected') }}
                            </strong>
                        </small>
                        <p-button
                            [label]="returnOutcomeActionLabel()"
                            icon="pi pi-check"
                            [loading]="completingReturnOutcome()"
                            [disabled]="!canCompleteReturnOutcome()"
                            (click)="completeReturnOutcome()"></p-button>
                    </div>
                </div>
            }
        </app-page-data-panel>
    </div>


    <div class="editing-workbench">
        <app-page-data-panel title="Schedule Values" subtitle="Plan and mapping values for this delivery schedule.">
            <div class="section-box section-box-context">
                <div class="detail-summary">
                    <div><span>Stand Qty</span><strong>{{ schedule().standQty ?? schedule().projectItem?.quantity ?? '-' }}</strong></div>
                    <div><span>CRM Supplier</span><strong>{{ schedule().supplierClientName || schedule().supplier || '-' }}</strong></div>
                    <div><span>Contact Override</span><strong>{{ schedule().contactNumberOverride || '-' }}</strong></div>
                    <div><span>Installation Date Override</span><strong>{{ formattedDate(schedule().plannedInstallationDateOverride) }}</strong></div>
                    @if (supportsRemovalFlow()) {
                        <div><span>Removal Date Override</span><strong>{{ formattedDate(schedule().plannedRemovalDateOverride) }}</strong></div>
                        <div><span>Return Destination</span><strong>{{ schedule().returnDestinationType || '-' }}</strong></div>
                        <div><span>Return Disposition</span><strong>{{ schedule().returnDisposition || '-' }}</strong></div>
                        <div><span>Return Warehouse / Location</span><strong>{{ schedule().returnWarehouseName || schedule().returnLocationName || '-' }}</strong></div>
                        <div><span>Return Condition</span><strong>{{ schedule().returnConditionName || '-' }}</strong></div>
                    }
                    <div class="full-detail-row"><span>Delivery Comment</span><strong>{{ schedule().comment || '-' }}</strong></div>
                </div>
            </div>
        </app-page-data-panel>
        <app-page-data-panel
            title="Allocated Materials"
            subtitle="Assign physical reserved stock units or logical external coverage to this delivery schedule. This does not create a new stock reservation.">
            <div class="section-box section-box-allocation">
            <div class="allocation-context">
                <div class="allocation-context-item">
                    <span>Warehousing Stand / Item</span>
                    <button type="button" class="record-link" (click)="openItem()" [disabled]="!(schedule().inventoryItem?.id || selectedItem()?.inventoryItem?.id)">
                        <strong>{{ itemContextLabel() }}</strong>
                        <i class="pi pi-external-link"></i>
                    </button>
                </div>
                <div class="allocation-summary">
                    <div>
                        <span>Coverage Mode</span>
                        <p-tag [value]="coverageModeLabel()" [severity]="coverageModeSeverity()"></p-tag>
                    </div>
                    <div>
                        <span>Required</span>
                        <strong>{{ formatQuantity(schedule().requiredQuantity) }}</strong>
                    </div>
                    <div>
                        <span>Allocated</span>
                        <strong>{{ formatQuantity(schedule().allocatedQuantity) }}</strong>
                    </div>
                    <div>
                        <span>Remaining</span>
                        <strong>{{ formatQuantity(schedule().remainingAllocationQuantity) }}</strong>
                    </div>
                    @if (schedule().allocationActionRequired !== false) {
                    <div>
                        <span>Status</span>
                        <p-tag [value]="allocationLabel()" [severity]="allocationSeverity(schedule().allocationStatus)"></p-tag>
                    </div>
                    }
                </div>
            </div>
            </div>

            @if (schedule().allocationActionRequired !== false && schedule().allocationWarnings?.length) {
                <div class="warning-panel mt-3">
                    <strong>Allocation Warnings</strong>
                    <ul>
                        @for (warning of schedule().allocationWarnings; track warning) {
                            <li>{{ warning }}</li>
                        }
                    </ul>
                </div>
            }

            <div class="allocation-grid mt-3">
                <section class="allocation-card allocation-card-current">
                    <div class="allocation-card-header">
                        <div>
                            <h4>Current Allocations</h4>
                            <p>{{ isLogicalCoverage() ? 'Logical third-party coverage already assigned to this schedule.' : 'Exact reserved stock units already assigned to this schedule.' }}</p>
                        </div>
                        <p-tag [value]="allocations().length + ' lines'" severity="info"></p-tag>
                    </div>
                    @if (allocations().length) {
                        <div class="allocation-list">
                            @for (allocation of allocations(); track allocation.id) {
                                <div class="allocation-row">
                                    <div class="allocation-row-main">
                                        <strong>{{ sourceLabel(allocation) }}</strong>
                                        <span>{{ allocation.sourceType || '-' }} | {{ coverageModeLabel(allocation) }}</span>
                                        <small>{{ itemContextLabel() }}</small>
                                        @if (reservationForAllocation(allocation); as reservation) {
                                            <div class="allocation-stock-unit-block">
                                                <span class="allocation-stock-unit-label">{{ allocationSummaryLabel(allocation) }}</span>
                                                <div class="allocation-stock-unit-list">
                                                    <div class="allocation-stock-unit-pill">
                                                        {{ reservationPillLabel(reservation) }}
                                                    </div>
                                                </div>
                                            </div>
                                        } @else if (allocation.stockUnitId || allocation.stockUnitCode) {
                                            <div class="allocation-stock-unit-block">
                                                <span class="allocation-stock-unit-label">{{ allocationSummaryLabel(allocation) }}</span>
                                                <div class="allocation-stock-unit-list">
                                                    <div class="allocation-stock-unit-pill">
                                                        {{ allocationReservationLabel(allocation) }}
                                                    </div>
                                                </div>
                                            </div>
                                        } @else if (isLogicalCoverage(allocation)) {
                                            <div class="allocation-stock-unit-block">
                                                <span class="allocation-stock-unit-label">{{ allocationSummaryLabel(allocation) }}</span>
                                                <div class="allocation-stock-unit-list">
                                                    <div class="allocation-stock-unit-pill">
                                                        {{ allocationReservationLabel(allocation) }}
                                                    </div>
                                                </div>
                                            </div>
                                        } @else {
                                            <small class="allocation-legacy-note">Legacy allocation without exact stock-unit binding.</small>
                                        }
                                    </div>
                                    <div class="allocation-row-metric">
                                        <span>Allocated</span>
                                        <strong>{{ formatQuantity(allocation.allocatedQuantity) }}</strong>
                                    </div>
                                    <p-button
                                        label="Release"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        [outlined]="true"
                                        [loading]="savingAllocationId() === allocation.id"
                                        (click)="removeAllocation(allocation)"></p-button>
                                </div>
                            }
                        </div>
                    } @else {
                        <div class="allocation-empty">{{ isLogicalCoverage() ? 'No logical source coverage has been assigned to this schedule yet.' : 'No reserved material has been allocated to this schedule yet.' }}</div>
                    }
                </section>

                <section class="allocation-card allocation-card-candidate">
                    <div class="allocation-card-header">
                        <div>
                            <h4>{{ isLogicalCoverage() ? 'Logical Coverage Candidates' : 'Reserved Stock Unit Candidates' }}</h4>
                            <p>{{ isLogicalCoverage() ? 'Only confirmed third-party source coverage is available here.' : 'Only stock units already reserved by the warehouse team are available here.' }}</p>
                        </div>
                        <p-button icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" [loading]="loadingAllocations()" (click)="loadAllocations()"></p-button>
                    </div>
                    @if (allocationCandidates().length) {
                        <div class="allocation-list">
                            @for (candidate of allocationCandidates(); track candidate.reservationAllocationId || candidate.projectItemSourceId || $index) {
                                <div class="allocation-candidate">
                                    <div class="allocation-candidate-main">
                                        <div>
                                            <strong>{{ sourceLabel(candidate) }}</strong>
                                            <span>{{ candidate.sourceType || '-' }} | {{ coverageModeLabel(candidate) }}</span>
                                        </div>
                                        <small>{{ itemContextLabel() }}</small>
                                    </div>
                                    <div class="allocation-stock-unit-block">
                                        <span class="allocation-stock-unit-label">{{ candidateSummaryLabel(candidate) }}</span>
                                        <div class="allocation-stock-unit-list">
                                            <div class="allocation-stock-unit-pill">
                                                {{ allocationReservationLabel(candidate) }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="allocation-metrics">
                                        <div><span>Reserved</span><strong>{{ formatQuantity(candidate.reservedQuantity) }}</strong></div>
                                        <div><span>Allocated elsewhere</span><strong>{{ formatQuantity(candidate.allocatedToOtherSchedules) }}</strong></div>
                                        <div><span>Available</span><strong>{{ formatQuantity(candidate.availableToAllocate) }}</strong></div>
                                    </div>
                                    <div class="allocation-action">
                                        <div class="allocation-qty">
                                            <span>Allocate Qty</span>
                                            <input
                                                pInputText
                                                type="number"
                                                min="0"
                                                [max]="maxAllocationForCandidate(candidate)"
                                                [ngModel]="allocationQuantities()[(candidate.reservationAllocationId || candidate.projectItemSourceId || 0)] || 0"
                                                (ngModelChange)="updateAllocationQuantity((candidate.reservationAllocationId || candidate.projectItemSourceId), $event)"
                                                [ngModelOptions]="{ standalone: true }" />
                                        </div>
                                        <p-button
                                            label="Allocate"
                                            icon="pi pi-link"
                                            [disabled]="maxAllocationForCandidate(candidate) <= 0"
                                            [loading]="savingAllocationId() === (candidate.reservationAllocationId || candidate.projectItemSourceId)"
                                            (click)="allocateCandidate(candidate)"></p-button>
                                    </div>
                                </div>
                            }
                        </div>
                    } @else {
                        <div class="allocation-empty">
                            {{ isLogicalCoverage()
                                ? 'No confirmed third-party source coverage is available yet. Confirm source coverage in POSM materials, then return here.'
                                : 'No reserved stock units are available. Reserve stock first in Warehousing > Reservations, then return here.' }}
                        </div>
                    }
                </section>
            </div>
        </app-page-data-panel>
    </div>

    @if (projectId && lineId && companyId) {
        <app-field-resources-panel
            targetType="POSM_DELIVERY_SCHEDULE"
            [targetId]="lineId"
            [targetReference]="schedule().scheduleNumber"
            [locationId]="schedule().locationId || schedule().location?.id"
            [locationName]="schedule().location?.name || schedule().storeName">
        </app-field-resources-panel>

        <app-posm-schedule-completion-report
            [projectId]="projectId"
            [scheduleId]="lineId"
            [owningCompanyId]="companyId">
        </app-posm-schedule-completion-report>
    }

    <p-dialog
        [header]="viewerDialogHeader()"
        [modal]="true"
        [visible]="showViewerDialog()"
        (visibleChange)="$event ? showViewerDialog.set(true) : closeViewerDialog()"
        [style]="{ width: '96vw', maxWidth: '1500px' }"
        [contentStyle]="{ padding: '0', overflow: 'hidden' }"
        appendTo="body">
        @if (viewerDialogUrl()) {
            <iframe
                class="embedded-view-frame"
                [src]="viewerDialogUrl()"
                [title]="viewerDialogHeader()"></iframe>
        }
    </p-dialog>

</app-page-shell>
